import {GameObjectType} from "@repo/shared";

import {ShardCollected} from "../../types";
import {Vec2} from "../core/Vec2.js";
import {ContactPoint, Material} from "../game/Line.js";
import {Player} from "../player/Player.js";
import {attractionStrength} from "../utils/AttractionFunction";
import {World} from "./World.js";

// --- Event System ---
export enum SimulationEventType {
  PlayerHitShard = "PlayerHitShard",
  PlayerOutOfBounds = "PlayerOutOfBounds",
  PlayerHitHazard = "PlayerHitHazard",
  PlayerHitLine = "PlayerHitLine",
  PlayerInAttractorZone = "PlayerInAttractorZone",
}

export interface SimulationEvent {
  type: SimulationEventType;
  player: Player;
  shardId?: number;
  timeOfImpact?: number;
  hazardType?: string;
  contact?: ContactPoint;
  material?: Material;
  impulse?: Vec2;
}

/**
 * A snapshot of a single Player's state at a specific step.
 */
interface PlayerStateSnapshot {
  currentPosition: Vec2;
  previousPosition: Vec2;
  velocity: Vec2;
  collectedShards: Set<number>;
  collectedShardsWithSteps: ShardCollected[];
  isDead: boolean;
  isCompleted: boolean;
  attractionActive: boolean;
  isActive: boolean;
}

/**
 * A snapshot of the entire simulation's state at a specific step.
 */
export interface SimulationSnapshot {
  step: number;
  isWon: boolean;
  isLost: boolean;
  playerStates: Map<string, PlayerStateSnapshot>;
}

/**
 * A deterministic simulation engine for a single, self-contained game round.
 * Once constructed, its world and player set are immutable.
 * Its sole purpose is to advance the simulation state by one step at a time.
 */
export class SimulationManager {
  public readonly world: World;
  public readonly players: Map<string, Player>;
  public currentStep = 0;
  public isWon = false;
  public isLost = false;

  // --- Simulation Constants ---
  private readonly STEP_SIZE = 1 / 50;
  private readonly GRAVITY = new Vec2(0, -9.81);
  private readonly DAMPING_FACTOR = 0.02;

  /**
   * Creates a new, isolated simulation instance.
   * @param world The static geometry of the world for this simulation.
   * @param initialPlayers An array of Player instances that will participate.
   */
  constructor(world: World, initialPlayers: Player[]) {
    this.world = world;
    this.players = new Map();

    const spawnPosition = this.world.getSpawnPosition();
    for (const player of initialPlayers) {
      player.reset(spawnPosition);
      this.players.set(player.id, player);
    }
  }

  /**
   * Resets the simulation state back to step 0.
   * This does NOT change the world or the set of players.
   */
  public reset(): void {
    this.currentStep = 0;
    this.isWon = false;
    this.isLost = false;
    const spawnPos = this.world.getSpawnPosition();
    for (const player of this.players.values()) {
      player.reset(spawnPos);
    }
  }

  public getLastShardCollectedStep(): number {
    const collectedShards: Record<number, number> = {};
    for (const player of this.players.values()) {
      for (const {steps, shardId} of player.collectedShardsWithSteps) {
        if (steps < (collectedShards[shardId] ?? Infinity))
          collectedShards[shardId] = steps;
      }
    }
    return Math.max(...Object.values(collectedShards));
  }

  /**
   * Advances the simulation by one discrete step and returns events for the GameManager.
   */
  public runStep(actionsForStep: Record<string, boolean>): SimulationEvent[] {
    const externalEvents: SimulationEvent[] = [];

    // --- NEW: First Pass - Activate Players ---
    // This loop checks for the first action and activates the player.
    for (const player of this.players.values()) {
      if (player.isDead || player.isCompleted || player.isActive) continue;
      if (actionsForStep[player.id]) player.activate();
    }

    // --- 1. Apply Forces and Update Positions ---
    for (const player of this.players.values()) {
      if (player.isDead || player.isCompleted || !player.isActive) continue;

      player.attractionActive = false;
      const isTabbing = actionsForStep[player.id] ?? false;

      if (isTabbing) {
        // World attractor forces
        this.world.getAttractors(player).forEach((attractor) => {
          const attractorEvents = attractor.process(player);
          for (const event of attractorEvents) {
            if (event.type === SimulationEventType.PlayerInAttractorZone) {
              player.addImpulse(
                event.impulse!.clone().mul(this.STEP_SIZE),
                GameObjectType.ATTRACTOR,
              );
            }
            externalEvents.push(event);
          }
        });

        // Player-to-player attraction
        this.processInterPlayerAttraction(player);
      }
    }

    // --- Update positions outside the loop
    for (const player of this.players.values()) {
      if (player.isDead || player.isCompleted || !player.isActive) continue;
      player.updatePosition(this.GRAVITY, this.STEP_SIZE, this.DAMPING_FACTOR);
    }

    // --- 2. Resolve Collisions & Generate External Events ---
    for (const player of this.players.values()) {
      if (player.isDead || player.isCompleted || !player.isActive) continue;

      // Line Collisions
      this.world.getLines(player).forEach((line) => {
        const lineEvents = line.process(player);
        for (const event of lineEvents) {
          if (event.type === SimulationEventType.PlayerHitLine)
            this.resolveLineCollision(player, event.contact!, event.material!);
          externalEvents.push(event);
        }
      });

      // Shard Collisions
      this.world.getShards(player).forEach((shard) => {
        externalEvents.push(...shard.process(player));
      });

      // Out of Bounds Check
      if (this.world.isPlayerOutOfBounds(player)) {
        externalEvents.push({
          type: SimulationEventType.PlayerOutOfBounds,
          player,
        });
      }
    }

    this.processSimulationEvents(externalEvents);
    this.updateGameStatus();

    this.currentStep++;
    return externalEvents;
  }

  private processInterPlayerAttraction(player: Player): void {
    const MAX_STRENGTH = 80;
    const ANGLE_OFFSET = 0.05;

    if (player.attractionActive) return;

    for (const otherPlayer of this.players.values()) {
      if (
        !otherPlayer.isActive ||
        otherPlayer.isDead ||
        otherPlayer.isCompleted ||
        otherPlayer === player
      )
        continue;

      const delta = otherPlayer.currentPosition
        .clone()
        .sub(player.currentPosition);

      if (delta.length() === 0) continue;

      const dist = delta.length();
      const strength = attractionStrength(dist, MAX_STRENGTH);

      const relativeVelocity = otherPlayer.velocity
        .clone()
        .sub(player.velocity);
      const rotationSign = Math.sign(delta.cross(relativeVelocity)) || 1;

      const impulse = delta
        .rotate(-rotationSign * ANGLE_OFFSET)
        .normalize()
        .mul(this.STEP_SIZE * strength);

      player.addImpulse(impulse, GameObjectType.ATTRACTOR);
    }
  }

  private resolveLineCollision(
    player: Player,
    contact: ContactPoint,
    material: Material,
  ): void {
    // This logic is unchanged from the previous version.
    player.currentPosition
      .add(contact.normal.clone().mul(contact.penetration))
      .finalize();
    if (contact.velocityAlongNormal < 0) {
      const restitution = material.restitution;
      const friction = material.friction;
      const impulseMag = -contact.velocityAlongNormal * (1 + restitution);
      const normalImpulse = contact.normal.clone().mul(impulseMag);
      const tangent = new Vec2(-contact.normal.y, contact.normal.x);
      const vt = player.velocity.dot(tangent);
      let jt = -vt;
      const maxFrictionImpulse = Math.abs(impulseMag) * friction;
      jt = Math.max(-maxFrictionImpulse, Math.min(jt, maxFrictionImpulse));
      const frictionImpulse = tangent.clone().mul(jt);

      player.addImpulse(
        normalImpulse.add(frictionImpulse),
        GameObjectType.PLASTIC,
      );
    }
  }

  private processSimulationEvents(events: SimulationEvent[]): void {
    for (const event of events) {
      switch (event.type) {
        case "PlayerHitShard":
          event.player.recordShardCollection(
            event.shardId!,
            this.currentStep,
            event.timeOfImpact!,
          );
          break;
        case "PlayerOutOfBounds":
        case "PlayerHitHazard":
          event.player.markAsDead();
          break;
      }
    }
  }

  /**
   * Checks the current state of all players to see if a win or loss
   * condition has been met for the entire round.
   */
  private updateGameStatus(): void {
    if (this.isWon || this.isLost) return; // Already decided

    const allCollectedShards = new Set<number>();
    let atLeastOnePlayerActive = false;
    let allActivePlayersAreDead = true;

    for (const player of this.players.values()) {
      player.collectedShards.forEach((id) => allCollectedShards.add(id));
      if (player.isActive) {
        atLeastOnePlayerActive = true;
        if (!player.isDead) {
          allActivePlayersAreDead = false;
        }
      }
    }

    // Win Condition: all shards have been collected.
    if (allCollectedShards.size >= this.world.getShardCount()) {
      this.isWon = true;
      for (const player of this.players.values()) {
        player.markAsCompleted();
      }
    }
    // Loss Condition: at least one player was active, and all of them are now dead.
    else if (atLeastOnePlayerActive && allActivePlayersAreDead) {
      this.isLost = true;
    }
  }

  /**
   * Captures the complete state of the simulation at the current step.
   * This method performs deep copies of mutable objects to prevent reference issues.
   * @returns A snapshot object representing the current simulation state.
   */
  public getStateSnapshot(): SimulationSnapshot {
    const playerStates = new Map<string, PlayerStateSnapshot>();

    for (const [playerId, player] of this.players.entries()) {
      playerStates.set(playerId, {
        // Use .clone() for all Vec2 objects to ensure we're not just copying references.
        currentPosition: player.currentPosition.clone(),
        previousPosition: player.previousPosition.clone(),
        velocity: player.velocity.clone(),
        // Create new Set/Array to copy the collections.
        collectedShards: new Set(player.collectedShards),
        collectedShardsWithSteps: [...player.collectedShardsWithSteps],
        // Copy primitive values.
        isDead: player.isDead,
        isCompleted: player.isCompleted,
        attractionActive: player.attractionActive,
        isActive: player.isActive,
      });
    }

    return {
      step: this.currentStep,
      isWon: this.isWon,
      isLost: this.isLost,
      playerStates: playerStates,
    };
  }

  /**
   * Overwrites the current simulation state with the data from a snapshot.
   * This effectively "time travels" the simulation to a previous state.
   * @param snapshot The state snapshot to load.
   */
  public loadStateSnapshot(snapshot: SimulationSnapshot): void {
    if (!snapshot) {
      console.error(
        "[SimManager] Attempted to load a null or undefined snapshot.",
      );
      return;
    }

    this.currentStep = snapshot.step;
    this.isWon = snapshot.isWon;
    this.isLost = snapshot.isLost;

    for (const [playerId, playerState] of snapshot.playerStates.entries()) {
      const player = this.players.get(playerId);
      if (player) {
        // Overwrite the player's state with the snapshot data.
        // Again, use .copyFrom() or .clone() for objects to avoid reference sharing.
        player.currentPosition.copyFrom(playerState.currentPosition);
        player.previousPosition.copyFrom(playerState.previousPosition);
        player.velocity.copyFrom(playerState.velocity);
        player.collectedShards = new Set(playerState.collectedShards);
        player.collectedShardsWithSteps = [
          ...playerState.collectedShardsWithSteps,
        ];
        player.isDead = playerState.isDead;
        player.isCompleted = playerState.isCompleted;
        player.attractionActive = playerState.attractionActive;
        player.isActive = playerState.isActive;
      }
    }
  }
}
